import { Bell, X, Truck, Store, UtensilsCrossed, Phone, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import type { PublicOrder } from "@/hooks/usePublicOrderNotifications";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface PublicOrderNotificationPanelProps {
  orders: PublicOrder[];
  hasNewOrders: boolean;
  onDismiss: (orderId: string) => void;
  onClearAll: () => void;
}

export function PublicOrderNotificationPanel({
  orders,
  hasNewOrders,
  onDismiss,
  onClearAll,
}: PublicOrderNotificationPanelProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const getOrderTypeIcon = (order: PublicOrder) => {
    if (order.table_number) {
      return <UtensilsCrossed className="h-4 w-4" />;
    }
    if (order.order_type === "tab") {
      return <Truck className="h-4 w-4" />;
    }
    return <Store className="h-4 w-4" />;
  };

  const getOrderTypeLabel = (order: PublicOrder) => {
    if (order.table_number) {
      return `Mesa ${order.table_number}`;
    }
    if (order.order_type === "tab") {
      return "Delivery";
    }
    return "Retirada";
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant={hasNewOrders ? "default" : "outline"}
          size="sm"
          className={`relative ${hasNewOrders ? "animate-pulse bg-orange-500 hover:bg-orange-600" : ""}`}
        >
          <Bell className={`h-4 w-4 ${hasNewOrders ? "animate-bounce" : ""}`} />
          {orders.length > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {orders.length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader className="flex flex-row items-center justify-between">
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Pedidos do Cardápio Digital
          </SheetTitle>
          {orders.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearAll}>
              Limpar Todos
            </Button>
          )}
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-8rem)] mt-4">
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Bell className="h-12 w-12 mb-4 opacity-30" />
              <p>Nenhum pedido novo do cardápio digital</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <Card key={order.id} className="relative border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={() => onDismiss(order.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="gap-1">
                        {getOrderTypeIcon(order)}
                        {getOrderTypeLabel(order)}
                      </Badge>
                      <Badge variant="secondary">
                        Pedido #{order.order_number}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {order.customer_name && (
                      <div className="font-medium">{order.customer_name}</div>
                    )}
                    
                    {order.customer_phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {order.customer_phone}
                      </div>
                    )}
                    
                    {order.delivery_address && (
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{order.delivery_address}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {format(new Date(order.created_at), "HH:mm", { locale: ptBR })}
                      </div>
                      <div className="font-bold text-primary">
                        {formatPrice(order.total)}
                      </div>
                    </div>
                    
                    <Button
                      className="w-full mt-2"
                      size="sm"
                      onClick={() => {
                        window.location.href = "/dashboard/pedidos";
                      }}
                    >
                      Ver Detalhes
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
